import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef } from 'react'

import { playCharacterTunnelEnterSound } from './audio/characterEventSound'
import { store } from '../stores/store'
import { playSound, stopSound } from '../utils/soundPlayer'
import { useRunState } from './provider/useRunState'

const TUNNEL_EXIT_EARLY_SECONDS = 0.4

function isActiveRunPhase(phase: string): boolean {
    return phase === 'running' || phase === 'levelTransition'
}

export const TunnelTransitionSoundBridge = observer(function TunnelTransitionSoundBridge() {
    const runState = useRunState()
    const isRun = store.appFlowPhase === 'run'
    const activeTransition = runState.trackRegionState.activeTransition
    const previousTransitionRef = useRef(activeTransition)
    const tunnelSpeedPlayingRef = useRef(false)
    const tunnelExitPlayedRef = useRef(false)
    const progressSampleRef = useRef<{ progress: number; time: number } | null>(null)

    useEffect(() => {
        return () => {
            stopSound('tunnelSpeed')
        }
    }, [])

    useEffect(() => {
        const inTunnel = isRun && isActiveRunPhase(runState.phase) && activeTransition

        if (inTunnel) {
            if (!tunnelSpeedPlayingRef.current) {
                playSound('tunnelSpeed')
                tunnelSpeedPlayingRef.current = true
            }
        } else if (tunnelSpeedPlayingRef.current) {
            stopSound('tunnelSpeed')
            tunnelSpeedPlayingRef.current = false
        }
    }, [activeTransition, isRun, runState.phase])

    useEffect(() => {
        if (!isRun) {
            previousTransitionRef.current = false
            tunnelExitPlayedRef.current = false
            progressSampleRef.current = null
            return
        }

        return reaction(
            () => ({
                phase: runState.phase,
                activeTransition: runState.trackRegionState.activeTransition,
                transitionProgress: runState.trackRegionState.transitionProgress,
            }),
            ({ phase, activeTransition: inTransition, transitionProgress }) => {
                if (!isActiveRunPhase(phase)) {
                    return
                }

                const wasInTransition = previousTransitionRef.current

                if (inTransition && !wasInTransition) {
                    playSound('tunnelEnter')
                    playCharacterTunnelEnterSound()
                    tunnelExitPlayedRef.current = false
                    progressSampleRef.current = null
                } else if (!inTransition && wasInTransition) {
                    if (!tunnelExitPlayedRef.current) {
                        playSound('tunnelExit')
                    }
                    tunnelExitPlayedRef.current = false
                    progressSampleRef.current = null
                } else if (inTransition && !tunnelExitPlayedRef.current) {
                    const now = performance.now()
                    const previousSample = progressSampleRef.current

                    if (
                        previousSample &&
                        transitionProgress > previousSample.progress + 0.001
                    ) {
                        const elapsedSeconds = (now - previousSample.time) / 1000
                        const progressRate =
                            (transitionProgress - previousSample.progress) / elapsedSeconds

                        if (progressRate > 0) {
                            const remainingSeconds = (1 - transitionProgress) / progressRate

                            if (remainingSeconds <= TUNNEL_EXIT_EARLY_SECONDS) {
                                playSound('tunnelExit')
                                tunnelExitPlayedRef.current = true
                            }
                        }
                    }

                    if (!tunnelExitPlayedRef.current) {
                        progressSampleRef.current = {
                            progress: transitionProgress,
                            time: now,
                        }
                    }
                }

                previousTransitionRef.current = inTransition
            },
        )
    }, [isRun, runState])

    return null
})
